import { ChangeDetectorRef, Component, NgZone, OnDestroy, ViewChild } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { ENV } from '@app/env';
import { AppVersion } from '@ionic-native/app-version';
import { Device } from '@ionic-native/device';
import { Globalization } from '@ionic-native/globalization';
import { InAppBrowser } from '@ionic-native/in-app-browser';
import { Market } from '@ionic-native/market';
import { SafariViewController } from '@ionic-native/safari-view-controller';
import { SplashScreen } from '@ionic-native/splash-screen';
import { StatusBar } from '@ionic-native/status-bar';
import { TranslateService } from '@ngx-translate/core';
import {
  Alert,
  AlertController,
  App,
  Config,
  Events,
  Loading,
  LoadingController,
  MenuController,
  Modal,
  ModalController,
  Nav,
  Platform,
  Popover,
  PopoverController,
  Toast,
  ToastController,
  ToastOptions,
  ViewController,
} from 'ionic-angular';
import * as moment from 'moment';
import { interval } from 'rxjs/observable/interval';
import { APP_CONFIG } from './app.config';
import { OrderLocalConfig } from './pages/orders/models/order-config.model';
import { Place } from './pages/place/models/place.model';
import { ReservationConfigType } from './pages/reservation/enums/reservation-config-type.enum';
import { ReservationConfig } from './pages/reservation/models/reservation-config.model';
import { ReservationService } from './pages/reservation/reservation.service';
import { Store } from './pages/store/models/store.model';
import { PopoverOption } from './shared/components/popover/popover.model';
import { AlertSubscriptionType } from './shared/enums/alert-subscription-type.enum';
import { Authority } from './shared/enums/authority.enum';
import { FeatureIdentifier } from './shared/enums/feature-identifier.enum';
import { GaEvents } from './shared/enums/ga-events.enum';
import { MenuType } from './shared/enums/menu-type.enum';
import { PushType } from './shared/enums/push-type.enum';
import { SubscriptionStatus } from './shared/enums/subscription-status.enum';
import { VersionCompare } from './shared/enums/version-compare.enum';
import { UtilHelper } from './shared/helpers/utils.helper';
import { Error } from './shared/models/error.model';
import { FeatureInTest } from './shared/models/feature-in-test.model';
import { MenuItem } from './shared/models/menu-item';
import { Notification } from './shared/models/notification.model';
import { ModulesInTestToShow } from './shared/models/subscription.model';
import { UserLogged } from './shared/models/user-logged.model';
import { User } from './shared/models/user.model';
import { VersionFirebase } from './shared/models/version-firebase';
import { AnalyticsService } from './shared/services/analytics.service';
import { AppStateService } from './shared/services/app-state.service';
import { ConnectivityService } from './shared/services/connectivity.service';
import { IosSwipeBackService } from './shared/services/ios-swipe-back.service';
import { SignupOriginService } from './shared/services/signup-origin.service';
import { UserService } from './shared/services/user.service';

declare var Iugu: any;
declare var window: any;
@Component({ templateUrl: 'app.html' })
// tslint:disable-next-line:component-class-suffix
export class FastgetApp implements OnDestroy {
  @ViewChild(Nav) nav: Nav;

  user: UserLogged = null;
  rootPage: any;
  menuPageSelected: string = 'Home';
  showMenu: boolean = false;
  menus: Array<MenuItem> = [];
  MenuType: any = MenuType;

  showStoreSelect: boolean;

  private pushRegistered: boolean;
  private oneSignalWeb: any;
  private oneSignalNative: any;

  alreadyDisplayedAvailableUpdate: boolean;

  modulesInTest: ModulesInTestToShow;

  /**
   * Colocar os segments dos IonicPages que precisam ser acessados diretamente.
   * Foi a unica maneira que consegui fazer ficar na pagina sem carregar.
   * O que acontece, é que ao acessar a página diretamente, ele passa pelas validação de obter um usuário, e seta um novo rootPage, saindo assim
   * da tela que foi redirecionado.
   */
  private deeplinkSegments: Array<string> = [
    'home',
    'password',
    'establishment-registration',
    'place-home',
    'orders',
    'panel',
  ];
  private ignoreLoadUserSegments: Array<string> = [
    'password',
    'establishment-registration',
    'panel',
  ];

  constructor(
    private app: App,
    private appVersion: AppVersion,
    private analyticsService: AnalyticsService,
    private alertCtrl: AlertController,
    private appStateService: AppStateService,
    private changeDetection: ChangeDetectorRef,
    private config: Config,
    private connectivityService: ConnectivityService,
    private device: Device,
    private events: Events,
    private globalization: Globalization,
    private iab: InAppBrowser,
    private iosSwipeBackService: IosSwipeBackService,
    private loadingCtrl: LoadingController,
    private market: Market,
    private menuCtrl: MenuController,
    private modalCtrl: ModalController,
    private ngZone: NgZone,
    public platform: Platform,
    private popoverCtrl: PopoverController,
    private safariViewController: SafariViewController,
    private splashScreen: SplashScreen,
    private statusBar: StatusBar,
    private swUpdate: SwUpdate,
    private toastCtrl: ToastController,
    private translateService: TranslateService,
    public userService: UserService,
    private reservationService: ReservationService,
    private signupOriginService: SignupOriginService
  ) {
    // Antes de qualquer navegação trocar a URL: origem de marketing do cadastro (só na web)
    if (!this.platform.is('cordova')) {
      this.signupOriginService.capture();
    }

    //Workaround para problema do scroll nos inputs do teclado. See: https://github.com/ionic-team/ionic/issues/6228#issuecomment-297360295
    // all platforms
    this.config.set('scrollPadding', false);
    this.config.set('scrollAssist', false);
    this.config.set('autoFocusAssist', false);
    // android
    this.config.set('android', 'scrollAssist', true);
    this.config.set('android', 'autoFocusAssist', 'delay');

    //Força o sistema web acessado via browser celular a usar o theme do material design, comum em sistemas web, mesmo que esteja rodando em um iphone.
    if (location.hostname !== 'localhost' && this.platform.is('mobileweb')) {
      this.config.set('mobileweb', 'mode', 'md');
    }
    this.initializeApp();
  }

  initializeApp(): void {
    this.platform.ready().then(async () => {
      this.analyticsService.init();
      // Precisam iniciar antes de qualquer requisição: o checkConnection depende do connectivityService
      this.appStateService.init();
      this.connectivityService.init();
      // Antes de qualquer navegação: o guarda de borda precisa estar ativo já na primeira tela
      this.iosSwipeBackService.initialize();
      this.setMenuState();
      this.setupLanguages();
      this.setupStatusBar();
      this.listenAppEvents();

      await this.userService.loadLocalConfig();
      await this.getUser();

      this.setupIugu();
      this.checkCompatibleBrowser();
      this.setupInitialPage();

      await this.initFirebaseConfig();
      await this.checkVersion();
      await this.checkRemoteConfigs();

      this.initPushNotification();
      this.hideSplashScreen();
    });
  }

  private setMenuState(): void {
    const _showMenuState: string = localStorage.getItem('showMenu');
    // TODO: tratar submenus quando tela maior e deixar apenas parte do menu aparecendo
    this.showMenu = true; //this.platform.is('core') && (showMenuState == null || showMenuState == "true");
  }

  private setupLanguages(): void {
    this.globalization.getPreferredLanguage().then(
      (res) => {
        const _lang: string = this.getTranslationLanguage(res.value);
        //TODO: tirar hardcode abaixo - necessário traduzir todas as keys
        // this.translateService.setDefaultLang(lang);
        // this.translateService.use(lang);
        this.translateService.setDefaultLang('pt');
        this.translateService.use('pt');
      },
      (error) => {
        console.log(error);
        this.translateService.setDefaultLang('pt');
        this.translateService.use('pt');
      }
    );
  }

  private setupStatusBar(): void {
    if (this.platform.is('cordova')) {
      if (this.platform.is('android')) {
        this.statusBar.backgroundColorByHexString('#00000000');
      } else {
        this.statusBar.styleLightContent();
      }
    }
  }

  private hideSplashScreen(): void {
    if (this.platform.is('cordova')) {
      setTimeout(() => {
        this.splashScreen.hide();
      }, 1000);
    }
  }

  private setupInitialPage(): void {
    if (!this.isCurrentRedirectPageDeeplink(this.deeplinkSegments)) {
      if (this.user) {
        this.prepareHome();
      } else {
        this.rootPage = 'Login';
      }
    }
  }

  private checkCompatibleBrowser(): void {
    if (this.user) {
      if (
        navigator.userAgent.toLowerCase().indexOf('firefox') > -1 ||
        navigator.userAgent.indexOf('MSIE') > -1 ||
        !!document['documentMode']
      ) {
        const alert: Alert = this.alertCtrl.create({
          enableBackdropDismiss: false,
          title: 'Olá!',
          message:
            'Para uma melhor experiência recomendamos o uso dos navegadores Chrome, Safari ou Edge.',
          buttons: ['Ok, entendi'],
        });
        alert.present();
      }
    }
  }

  private async setupIugu(): Promise<void> {
    Iugu.setAccountID('5CCEE0C1523A408EAA3CD52FBFC04B7A');
    if (ENV.mode !== 'PROD') {
      Iugu.setTestMode(true);
    }
  }

  private prepareHome(): void {
    let page: string = '';
    if (this.userService.estabSelected) {
      page = 'Home';
      this.analyticsService.event(GaEvents.SELECT_STORE, this.userService.estabSelected.tradeName);
    } else if (this.userService.placeSelected) {
      page = 'PlaceHome';
      this.analyticsService.event(GaEvents.SELECT_PLACE, this.userService.placeSelected.tradeName);
    } else if (
      this.userService.user.establishments.length > 0 ||
      this.userService.user.places.length > 0
    ) {
      // Se não tem um estabelecimento selecionado e nem um local, mando para a home, e lá será tratado para mostrar a modal de Store para seleção
      page = 'Home';
    }

    if (!this.rootPage) this.rootPage = page;
    else this.nav.setRoot(page);

    this.checkShowSelectStores();
    this.menuPageSelected = page;
  }

  getSelectedStoreName(): string {
    return (
      (this.userService.estabSelected && this.userService.estabSelected.tradeName) ||
      (this.userService.placeSelected && this.userService.placeSelected.tradeName) ||
      'Selecione'
    );
  }

  private getAuthorityDescription(): string {
    return (
      (this.userService.estabSelected &&
        this.userService.estabSelected.authority &&
        this.userService.estabSelected.authority['description']) ||
      (this.userService.placeSelected &&
        this.userService.placeSelected.authority &&
        this.userService.placeSelected.authority['description']) ||
      ''
    );
  }

  private checkShowSelectStores(): void {
    this.showStoreSelect =
      this.user &&
      ((this.user.establishments && this.user.establishments.length > 1) ||
        (this.user.establishments &&
          this.user.establishments.length === 1 &&
          this.user.places &&
          this.user.places.length > 0) ||
        (this.user.places && this.user.places.length > 1));
  }

  private checkShowModulesInTest(): void {
    this.modulesInTest = {
      count: 0,
      nextModulesToExpireCount: 0,
      nextModulesToExpireDate: '',
      hasSubscriptionProblem: false,
      allModulesExpired: false,
    };
    if (this.userService.estabSelected) {
      let subscriptionProblemsStatus = [
        SubscriptionStatus.SUSPEND,
        SubscriptionStatus.CANCELED,
        SubscriptionStatus.REACTIVATING,
        SubscriptionStatus.TRIAL_EXPIRED,
        SubscriptionStatus.BLOCKED,
      ];
      try {
        this.modulesInTest.hasSubscriptionProblem =
          subscriptionProblemsStatus.indexOf(this.userService.estabSelected.subscriptionStatus) !=
          -1;

        if (
          this.userService.estabSelected.featureInTest &&
          this.userService.estabSelected.featureInTest.length > 0
        ) {
          let count: number = this.userService.estabSelected.featureInTest.filter(
            (f) => f.status == SubscriptionStatus.IN_TEST
          ).length;
          if (count > 0) {
            let nextModulesToExpireDate: string;

            this.userService.estabSelected.featureInTest.forEach((f) => {
              if (f.status == SubscriptionStatus.IN_TEST) {
                if (!nextModulesToExpireDate) {
                  nextModulesToExpireDate = f.endDate;
                } else {
                  let expireDate = moment(f.endDate, 'YYYY-MM-DD');
                  let nextDate = moment(nextModulesToExpireDate, 'YYYY-MM-DD');

                  if (expireDate.isBefore(nextDate)) {
                    nextModulesToExpireDate = f.endDate;
                  }
                }
              }
            });

            let nextModulesToExpireCount = this.userService.estabSelected.featureInTest.filter(
              (f) => f.endDate == nextModulesToExpireDate
            ).length;

            this.modulesInTest.count = count;
            this.modulesInTest.nextModulesToExpireCount = nextModulesToExpireCount;
            this.modulesInTest.nextModulesToExpireDate = moment(
              nextModulesToExpireDate,
              'YYYY-MM-DD'
            ).format('DD/MM');
          }
        }
        // Verifica se não é o caso onde todos os módulos estão expirados, caso a assinatura ainda esteja em teste
        if (this.userService.estabSelected.subscriptionStatus == SubscriptionStatus.IN_TEST) {
          let countModulesExpired = this.userService.estabSelected.featureInTest.filter(
            (f) => f.status == SubscriptionStatus.TRIAL_EXPIRED
          ).length;
          if (countModulesExpired == this.userService.estabSelected.featureInTest.length) {
            this.modulesInTest.allModulesExpired = true;
          }
        }
      } catch (error) {
        console.log(error);
        this.showToast({
          message:
            'Ocorreu uma falha ao carregar os módulos. Caso o erro persista, entre em contato com a FastGet.',
          duration: 4000,
        });
        throw error;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('onDestroy');
    //https://stackoverflow.com/a/42249082/6031927
    this.changeDetection.detach();
  }

  /**
   * Verifica se a url atual é um deeplink, para não redirecionar setando um novo rootPage. Ver comentário na declaração da variavel 'deeplinkPages'
   * @returns {boolean}
   */
  private isCurrentRedirectPageDeeplink(deepSegments: string[]): boolean {
    let currentSegment: string;
    if (location.hash && location.hash.length > 0) {
      const segments: Array<string> = location.hash.split('/');
      if (segments && segments.length > 1) {
        currentSegment = segments[1];
      }
    }
    if (currentSegment && currentSegment.length > 0) {
      for (const i in deepSegments) {
        if (currentSegment.indexOf(deepSegments[i]) !== -1) {
          return true;
        }
      }
    }
    return false;
  }

  private async getUser(forceUpdate?: boolean): Promise<any> {
    if (!this.isCurrentRedirectPageDeeplink(this.ignoreLoadUserSegments)) {
      this.user = await this.userService.loadUser(forceUpdate);
      this.configureMenuItems();
    }
  }

  showModalStores(): void {
    const modal: Modal = this.modalCtrl.create(
      'ModalStores',
      {},
      { enableBackdropDismiss: false, cssClass: 'no-shadow-modal business-type-modal' }
    );
    modal.onDidDismiss((selected: { establishment?: Store; place?: Place }) => {
      if (selected) {
        let page: string;
        if (selected.establishment) {
          page = 'Home';
          this.setAuthorityTagPushNotification();
        } else if (selected.place) {
          page = 'PlaceHome';
        }
        if (page) {
          this.configureMenuItems();
          this.nav.setRoot(page);
          this.menuPageSelected = page;
        }
      }
    });
    modal.present();
  }

  private checkVersionNews(): void {
    setTimeout(async () => {
      try {
        let version: string = APP_CONFIG.APP_VERSION;
        if (this.platform.is('cordova')) {
          version = await this.appVersion.getVersionNumber();
        }
        if (
          version == APP_CONFIG.APP_VERSION &&
          APP_CONFIG.SHOW_RELEASE_NOTES &&
          (!this.userService.localConfig.lastLocalVersion ||
            this.userService.localConfig.lastLocalVersion !== version)
        ) {
          this.modalCtrl
            .create(
              'WhatsNew',
              { version: version, from: 'Home' },
              { enableBackdropDismiss: false }
            )
            .present();
          this.userService.localConfig.hideHomeNewsVersion = false;
          this.userService.updateLocalConfig();
        }
      } catch (error) {
        console.log(error);
      }
    }, 1000);
  }

  showModalSubscriptionAlert(subscriptionStatus: SubscriptionStatus): void {
    const modal: Modal = this.modalCtrl.create(
      'ModalTrialExpired',
      { subscriptionStatus: subscriptionStatus },
      { cssClass: 'no-shadow-modal' }
    );
    modal.onDidDismiss((activate: any) => {
      if (activate) {
        switch (subscriptionStatus) {
          case SubscriptionStatus.SUSPEND:
          case SubscriptionStatus.REACTIVATING:
          case SubscriptionStatus.CANCELED:
          case SubscriptionStatus.BLOCKED:
            this.goToAccountPageForSeeSubscription();
            break;

          default:
            // Se o plano não está ativo, entra no fluxo de ativação de plano
            if (!this.platform.is('cordova')) {
              this.nav.push('PlanSubscription', {
                action: 'activateSubscription',
                activeModule: activate.activeModule,
              });
            } else {
              this.goToAccountPageForSeeSubscription();
            }
            break;
        }
      }
    });
    modal.present();
  }

  popoverEdit(event?: Event): void {
    if (event) event.stopPropagation();

    const options: Array<PopoverOption> = [];
    options.push(new PopoverOption(1, 'Alterar nome'));
    options.push(new PopoverOption(2, 'Alterar senha'));

    const popover: Popover = this.popoverCtrl.create('Popover', { actionOptions: options });
    popover.onDidDismiss((itemSelected: PopoverOption) => {
      if (itemSelected) {
        switch (itemSelected.id) {
          case 1:
            this.openAlertEditName();
            break;

          case 2:
            this.openAlertEditPassword();
            break;
        }
      }
    });
    popover.present({
      ev: event,
    });
  }

  openAlertEditName(): void {
    const alert: Alert = this.alertCtrl.create({
      title: 'Alterar nome',
      inputs: [
        {
          name: 'name',
          placeholder: 'Nome',
          value: this.user.userCompleteName,
        },
      ],
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          cssClass: 'dark',
        },
        {
          text: 'Salvar',
          handler: (data) => {
            const loading: Loading = this.loadingCtrl.create();
            loading.present();

            const user: User = new User({
              id: this.user.userId,
              name: data.name,
            });
            let storeId: number;
            let placeId: number;

            if (this.userService.estabSelected) {
              storeId = this.userService.estabSelected.id;
            } else {
              placeId = this.userService.placeSelected.id;
            }
            this.userService.updateUser(user, storeId, placeId, true).then(
              () => {
                this.getUser(true);
                loading.dismiss();
                alert.dismiss();
                if (!this.changeDetection['destroyed']) {
                  this.changeDetection.detectChanges();
                }
                this.showToast({
                  message: 'Nome alterado com sucesso!',
                  duration: 3000,
                });
              },
              (error: Error) => {
                console.log(error);
                this.showToast({
                  message: this.translateService.instant(
                    (error && error.message) || 'ERRORS.GENERIC'
                  ),
                  duration: 3000,
                });
                loading.dismiss();
              }
            );
            return false;
          },
        },
      ],
    });
    alert.present();
  }

  openAlertEditPassword(): void {
    const modalUpdatePassword: Modal = this.modalCtrl.create(
      'ModalUpdatePassword',
      { userId: this.user.userId },
      { enableBackdropDismiss: false, cssClass: 'no-shadow-modal' }
    );
    modalUpdatePassword.onDidDismiss((hadChanges: boolean) => {
      if (hadChanges) {
        this.getUser();
      }
    });
    modalUpdatePassword.present();
  }

  openPage(item: MenuItem): void {
    const currentPage: ViewController = this.nav.getActive();
    if (currentPage.id !== item.page) {
      if (item.hasSubMenu) {
        item.subMenuOpen = !item.subMenuOpen;
        return;
      }
      if (item.isLocked) {
        this.showModalFeatureLocked(item.page, item.modulesCanAccess);
      } else {
        this.analyticsService.event(GaEvents.OPEN_PAGE_MENU, 'Menu', item.page);
        if (item.page) {
          this.menuPageSelected = item.page;
          if (item.page == 'ReservationList') {
            this.goToReservationPage(item);
          } else {
            this.nav.setRoot(item.page, item.options);
          }
        } else if (item.externalUrl) {
          this.openBrowser(item.externalUrl);
        }
        this.menuCtrl.close();
      }
    }
  }

  /**
   * No app isso foi removido devido a restrições da Google para ter que usar as formas de pagamento deles
   */
  goToAccountPageForSeeSubscription(): void {
    if (this.platform.is('cordova')) {
      const alert: Alert = this.alertCtrl.create({
        title: 'Olá!',
        message: this.translateService.instant('GENERAL.GO_TO_WEBAPP_SEE_SUBSCRIPTION'),
        buttons: [
          {
            text: 'Agora não',
            role: 'cancel',
            cssClass: 'dark',
          },
          {
            text: 'OK, seguir para o Portal',
            handler: () => {
              this.iab.create(APP_CONFIG.WEBSITE.APP_WEB, '_system');
            },
          },
        ],
      });
      alert.present();
    } else {
      this.menuPageSelected = 'Account';
      this.nav.setRoot('Account');
    }
  }

  private async goToReservationPage(item: MenuItem): Promise<void> {
    const loading: Loading = this.loadingCtrl.create();
    try {
      await loading.present();
      const data: ReservationConfig = await this.reservationService.getReservationConfig(
        this.userService.estabSelected.id,
        false,
        ReservationConfigType.DEFAULT
      );
      if (item.page == 'ReservationConfig' || !data || data.status != 'ACTIVE') {
        this.nav.setRoot('ReservationConfig', item.options);
        this.menuPageSelected = 'ReservationConfig';
      } else {
        this.nav.setRoot(item.page, item.options);
        this.menuPageSelected = item.page;
      }
    } catch (error) {
      console.error(error);
      this.showToast({
        message: 'Falha ao abrir a tela de reservas',
        duration: 3000,
      });
    } finally {
      loading.dismiss();
    }
  }

  private configureMenuItems(): void {
    if (this.user) {
      this.menus = this.getMenuByAuthority();
    }
    this.checkShowModulesInTest();
  }

  private getMenuByAuthority(): Array<MenuItem> {
    let menus: Array<MenuItem> = [];
    let authority: Authority = null;
    let storeFeatureIdentifiers: Array<FeatureIdentifier> = null;
    let storeFeaturesInTest: Array<FeatureInTest> = null;

    if (this.userService.estabSelected) {
      authority = this.userService.estabSelected.authority;
      storeFeatureIdentifiers = this.userService.estabSelected.featureIdentifiers;
      storeFeaturesInTest = this.userService.estabSelected.featureInTest;
      menus = [
        {
          title: 'Início',
          page: 'Home',
          icon: 'home',
          iconiOS: 'ios-home-outline',
          authorities: [],
          modulesCanAccess: [],
        },
        {
          title: 'Cardápio',
          page: 'Category',
          icon: 'book',
          iconiOS: 'ios-book-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
          modulesCanAccess: [],
        },
        {
          title: 'Pedidos',
          page: null,
          icon: 'list-box',
          iconiOS: 'ios-list-box-outline',
          authorities: [],
          modulesCanAccess: [
            FeatureIdentifier.LOCAL_ORDER,
            FeatureIdentifier.DELIVERY_ORDER,
            FeatureIdentifier.FULL_ORDER,
          ],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Gestor de pedidos (KDS)',
              page: 'Orders',
              authorities: [
                Authority.ADMIN,
                Authority.MANAGER,
                Authority.ATTENDANT,
                Authority.KITCHEN,
                Authority.DELIVERYMAN,
                Authority.WAITER,
              ],
              modulesCanAccess: [
                FeatureIdentifier.LOCAL_ORDER,
                FeatureIdentifier.DELIVERY_ORDER,
                FeatureIdentifier.FULL_ORDER,
              ],
            },
            {
              title: 'Pagamentos pendentes',
              page: 'TablesPayment',
              icon: 'ios-apps',
              iconiOS: 'ios-apps',
              authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
              modulesCanAccess: [
                FeatureIdentifier.LOCAL_ORDER,
                FeatureIdentifier.DELIVERY_ORDER,
                FeatureIdentifier.FULL_ORDER,
              ],
            },
            {
              title: 'Cupons',
              page: 'Coupons',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [
                FeatureIdentifier.LOCAL_ORDER,
                FeatureIdentifier.DELIVERY_ORDER,
                FeatureIdentifier.FULL_ORDER,
              ],
            },
            {
              title: 'Estatísticas',
              page: 'Statistics',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Avaliações',
              page: 'Rating',
              icon: 'star',
              iconiOS: 'ios-star-outline',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [
                FeatureIdentifier.LOCAL_ORDER,
                FeatureIdentifier.DELIVERY_ORDER,
                FeatureIdentifier.FULL_ORDER,
              ],
            },
            {
              title: 'Configurações',
              page: 'OrdersConfig',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [
                FeatureIdentifier.LOCAL_ORDER,
                FeatureIdentifier.DELIVERY_ORDER,
                FeatureIdentifier.FULL_ORDER,
              ],
            },
          ],
        },
        {
          title: 'Reservas',
          page: null,
          icon: 'calendar',
          iconiOS: 'ios-calendar-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
          modulesCanAccess: [FeatureIdentifier.BOOKING_TABLE],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Gestor de Reservas',
              page: 'ReservationList',
              icon: 'calendar',
              iconiOS: 'ios-calendar-outline',
              authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
              modulesCanAccess: [FeatureIdentifier.BOOKING_TABLE],
            },
            {
              title: 'Estatísticas',
              page: 'ReservationStatistics',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Configurações',
              page: 'ReservationConfig',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [FeatureIdentifier.BOOKING_TABLE],
            },
          ],
        },
        {
          title: 'Fila de espera',
          page: null,
          icon: 'md-man',
          iconiOS: 'ios-man-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
          modulesCanAccess: [FeatureIdentifier.QUEUE],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Gestor de fila',
              page: 'Queue',
              authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
              modulesCanAccess: [FeatureIdentifier.QUEUE],
            },
            {
              title: 'Painel de senha',
              page: null,
              authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT],
              modulesCanAccess: [FeatureIdentifier.QUEUE],
              externalUrl: `${location.origin}/#/panel/${this.userService.estabSelected.storeUuid}/QUEUE`,
            },
            {
              title: 'Configurações',
              page: 'QueueConfig',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [FeatureIdentifier.QUEUE],
            },
          ],
        },
        {
          title: 'Atendimentos',
          page: null,
          icon: 'md-hand',
          iconiOS: 'ios-hand-outline',
          authorities: [],
          modulesCanAccess: [FeatureIdentifier.CALL_ATTENDANT],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Gestor de atendimentos',
              page: 'CallAttendant',
              authorities: [
                Authority.ADMIN,
                Authority.MANAGER,
                Authority.ATTENDANT,
                Authority.WAITER,
              ],
              modulesCanAccess: [FeatureIdentifier.CALL_ATTENDANT],
            },
            {
              title: 'Configurações',
              page: 'CallAttendantConfig',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [FeatureIdentifier.CALL_ATTENDANT],
            },
          ],
        },
        {
          title: 'Minha loja',
          page: null,
          icon: 'ios-restaurant',
          iconiOS: 'ios-restaurant-outline',
          hasDivider: true,
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Configurações',
              page: 'StoreDetails',
              icon: 'ios-restaurant',
              iconiOS: 'ios-restaurant-outline',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Meu plano',
              page: 'Account',
              icon: 'ios-create',
              iconiOS: 'ios-create-outline',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Usuários',
              page: 'User',
              icon: 'md-person-add',
              iconiOS: 'md-person-add',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Cadastrar nova loja',
              page: 'StoreInternRegistration',
              authorities: [Authority.ADMIN],
              modulesCanAccess: [],
            },
          ],
        },
        {
          title: 'Pagamentos',
          page: null,
          icon: 'logo-usd',
          iconiOS: 'logo-usd',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
          hasSubMenu: true,
          subMenuOpen: false,
          subMenus: [
            {
              title: 'Conta de Pagamentos',
              page: 'PaymentAccount',
              icon: 'logo-usd',
              iconiOS: 'logo-usd',
              authorities: [Authority.ADMIN, Authority.MANAGER],
              modulesCanAccess: [],
            },
            {
              title: 'Dados bancários',
              page: 'BankAccount',
              authorities: [Authority.ADMIN],
              modulesCanAccess: [],
            },
            //{ title: 'Pedidos (legado)', page: "LegacyReceivables", icon: 'logo-usd', iconiOS: 'logo-usd', authorities: [Authority.ADMIN, Authority.MANAGER], modulesCanAccess: [] }
          ],
        },
        {
          title: 'CRM',
          page: 'Customers',
          icon: 'md-contacts',
          iconiOS: 'md-contacts',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Carteira',
          page: 'Wallet',
          icon: 'md-card',
          iconiOS: 'ios-card',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Config. de notificações',
          page: 'NotificationConfig',
          icon: 'md-notifications',
          iconiOS: 'ios-notifications-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Ferramentas',
          page: 'Tools',
          icon: 'md-hammer',
          iconiOS: 'ios-hammer-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
          isNew: true,
        },
      ];
    } else if (this.userService.placeSelected) {
      authority = this.userService.placeSelected.authority;
      menus = [
        {
          title: 'Início',
          page: 'PlaceHome',
          icon: 'home',
          iconiOS: 'ios-home-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        // { title: 'Pedidos', page: "Orders", icon: 'list-box', iconiOS: 'ios-list-box-outline', authorities: [Authority.ADMIN, Authority.MANAGER, Authority.ATTENDANT, Authority.KITCHEN, Authority.DELIVERYMAN], modulesCanAccess: [] },
        {
          title: 'Configurações do Local',
          page: 'PlaceDetails',
          icon: 'ios-restaurant',
          iconiOS: 'ios-restaurant-outline',
          hasDivider: true,
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Dados bancários',
          page: 'BankAccount',
          icon: 'card',
          iconiOS: 'ios-card',
          authorities: [Authority.ADMIN],
          modulesCanAccess: [],
        },
        // { title: 'Recebíveis', page: "LegacyReceivables", icon: 'logo-usd', iconiOS: 'logo-usd', authorities: [Authority.ADMIN, Authority.MANAGER], modulesCanAccess: [] },
        {
          title: 'Estatísticas',
          page: 'PlaceStatistics',
          icon: 'trending-up',
          iconiOS: 'ios-trending-up',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Ferramentas',
          page: 'Tools',
          icon: 'md-hammer',
          iconiOS: 'ios-hammer-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
        {
          title: 'Usuários',
          page: 'User',
          icon: 'people',
          iconiOS: 'ios-people-outline',
          authorities: [Authority.ADMIN, Authority.MANAGER],
          modulesCanAccess: [],
        },
      ];
    }
    this.user.authorityDescription = this.getAuthorityDescription();
    if (!authority) return [];

    menus = menus.filter(
      (menu) =>
        menu.hasSubMenu ||
        menu.authorities.length == 0 ||
        menu.authorities.find((a) => a['code'] === authority['code'])
    );

    const foundAuthority: Authority = [Authority.ADMIN, Authority.MANAGER].find((aut) => {
      return aut['code'] == authority['code'];
    });

    if (!storeFeatureIdentifiers && !storeFeaturesInTest) return menus;

    const isManager = foundAuthority != null;

    for (let i = menus.length - 1; i >= 0; i--) {
      let m = menus[i];
      if (m.hasSubMenu && m.subMenus) {
        m.subMenus = m.subMenus.filter((menu) =>
          menu.authorities.find((a) => a['code'] === authority['code'])
        );
        if (m.subMenus.length == 0) {
          menus.splice(i, 1);
          continue;
        } else {
          m.subMenus.forEach((sm) => {
            sm = this.prepareMenuItem(sm, storeFeatureIdentifiers, storeFeaturesInTest);
            if (sm.page == this.menuPageSelected) {
              m.subMenuOpen = true;
            }
          });
        }
      }
      m = this.prepareMenuItem(m, storeFeatureIdentifiers, storeFeaturesInTest);

      // Não mostra menu com funcionalidades bloqueadas se não for um admin/manager
      if (m.isLocked && !isManager) {
        menus.splice(i, 1);
      }
    }
    return menus;
  }

  private prepareMenuItem(
    m: MenuItem,
    storeFeatureIdentifiers: Array<FeatureIdentifier>,
    storeFeaturesInTest: Array<FeatureInTest>
  ): MenuItem {
    if (m.modulesCanAccess.length === 0) {
      m.isLocked = false;
    } else {
      let hasFeature = (storeFeatureIdentifiers || []).some(
        (p) => m.modulesCanAccess.indexOf(p) !== -1
      );
      let isTesting = (storeFeaturesInTest || []).some(
        (f) =>
          m.modulesCanAccess.indexOf(f.identifiers) !== -1 && f.status == SubscriptionStatus.IN_TEST
      );
      m.isLocked = !hasFeature && !isTesting;
    }
    return m;
  }

  private showModalFeatureLocked(fromPage: string, features: Array<FeatureIdentifier>): void {
    if (this.userService.estabSelected) {
      if (
        this.userService.estabSelected.subscriptionStatus != SubscriptionStatus.ACTIVE &&
        this.userService.estabSelected.subscriptionStatus != SubscriptionStatus.IN_TEST
      ) {
        this.showModalSubscriptionAlert(this.userService.estabSelected.subscriptionStatus);
        return;
      }
    }
    const modal: Modal = this.modalCtrl.create(
      'ModalTrialExpired',
      {
        type: AlertSubscriptionType.LOCKED_FEATURE,
        fromPage: fromPage,
        features: features,
      },
      {
        cssClass: 'no-shadow-modal',
      }
    );
    modal.onDidDismiss((activate: any) => {
      if (activate) {
        this.nav.push('PlanSubscription');
      }
    });
    modal.present();
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
    localStorage.setItem('showMenu', this.showMenu ? 'true' : 'false');
    this.events.publish('menu:toggle');

    // https://stackoverflow.com/a/43846782/6031927
    // Faz resize da view, corrigindo o problema de não fazer o resize dos gráficos quando faz o toggle do menu
    if (typeof Event === 'function') {
      // modern browsers
      window.dispatchEvent(new Event('resize'));
    } else {
      // for IE and other old browsers
      // causes deprecation warning on modern browsers
      const evt: any = window.document.createEvent('UIEvents');
      evt.initUIEvent('resize', true, false, window, 0);
      window.dispatchEvent(evt);
    }
  }

  async seeNews(): Promise<void> {
    try {
      let version: string = APP_CONFIG.APP_VERSION;
      if (this.platform.is('cordova')) {
        version = await this.appVersion.getVersionNumber();
      }
      this.modalCtrl
        .create('WhatsNew', { version: version, from: 'Menu' }, { enableBackdropDismiss: false })
        .present();
    } catch (error) {
      console.log(error);
    }
  }

  listenAppEvents(): void {
    /**
     * Listener chamado após realizar login para atualizar os dados do usuário no AppComponent.
     */
    this.events.subscribe('user:login', async () => {
      console.log('login events');

      await this.getUser();
      this.prepareHome();
      this.initPushNotification();
      this.checkCompatibleBrowser();
    });
    /**
     * Listener chamado quando a sessão é expirado.
     */
    this.events.subscribe('user:sessionExpired', () => {
      this.unsubscribePush();
    });
    /**
     * Listener usado quando precisar navegar para uma tela sem acessar pelo menu, para assim deixar selecionado no menu a página acessada.
     */
    this.events.subscribe('changeMenuPage', (page) => {
      this.menuPageSelected = page;

      this.menus.forEach((m: MenuItem) => {
        if (m.hasSubMenu && m.subMenus) {
          let menu = m.subMenus.find((item) => item.page == page);
          if (menu) {
            m.subMenuOpen = true;
          }
        }
      });
    });
    /**
     * Listener usado quando atualizar infos do usuario
     */
    this.events.subscribe('user:updated', (user) => {
      this.user = user;
      this.user.authorityDescription = this.getAuthorityDescription();
      this.checkShowSelectStores();
      this.configureMenuItems();
    });
    this.events.subscribe('menu:hideMenu', (_user) => {
      if (this.showMenu) {
        this.toggleMenu();
      }
    });
    this.events.subscribe('user:showModalStores', () => {
      this.checkShowSelectStores();
      if (this.showStoreSelect) {
        this.showModalStores();
      }
    });
    this.events.subscribe('subscription:modalAlert', (subscriptionStatus: SubscriptionStatus) => {
      this.showModalSubscriptionAlert(subscriptionStatus);
    });
    this.events.subscribe('reservation:goTo', (item: any) => {
      this.goToReservationPage(item);
    });
    this.events.subscribe('app:checkVersionNews', () => {
      // Não mostra novidades no app
      if (!this.platform.is('cordova')) {
        this.checkVersionNews();
      }
    });
    this.events.subscribe('app:showModalMaintenance', () => {
      this.modalCtrl.create('Maintenance').present();
    });

    this.events.subscribe(
      'app:openMenuPage',
      (params: { page: string; isSubmenu: boolean; options: any }) => {
        console.log(this.menus);
        let menuItem: MenuItem;
        if (params.isSubmenu) {
          for (let m of this.menus) {
            if (m.hasSubMenu && m.subMenus) {
              menuItem = m.subMenus.find((sm) => sm.page == params.page);
              if (menuItem) {
                break;
              }
            }
          }
        } else {
          menuItem = this.menus.find((m) => m.page == params.page);
        }
        if (menuItem) {
          if (params.options) {
            menuItem.options = params.options;
          }
          this.openPage(menuItem);
        }
      }
    );
  }

  private getTranslationLanguage(language: string): string {
    console.log(language);
    language = language.substring(0, 2).toLowerCase();
    return APP_CONFIG.LANGUAGES.some((x) => x.code === language) ? language : 'pt';
  }

  logout(): void {
    const alert: Alert = this.alertCtrl.create({
      title: 'Sair',
      message: 'Você não receberá notificações enquanto estiver deslogado',
      buttons: [
        {
          text: 'Cancelar',
          role: 'cancel',
          cssClass: 'dark',
          handler: () => {
            console.log('Cancel clicked');
          },
        },
        {
          text: 'Sair',
          handler: () => {
            this.userService.logout().then(
              () => {
                this.unsubscribePush();
                this.user = null;
                this.app
                  .getRootNav()
                  .setRoot('Login')
                  .then(() => {
                    this.showToast({
                      message: 'Até mais!',
                      duration: 3000,
                    });
                  });
              },
              (_error) => {
                this.showToast({
                  message: 'Erro ao tentar deslogar.',
                  duration: 3000,
                });
              }
            );
          },
        },
      ],
    });
    alert.present();
  }

  goLoginPage(): void {
    this.nav.setRoot('Login');
  }

  showToast(config: ToastOptions, callbackDidDismiss?: VoidFunction): void {
    const toast: Toast = this.toastCtrl.create(config);
    toast.onDidDismiss((data, role) => {
      if (callbackDidDismiss) {
        if (role === 'close') {
          callbackDidDismiss();
        }
      }
    });
    toast.present();
  }

  private openBrowser(url: string): void {
    if (this.platform.is('cordova')) {
      this.safariViewController.isAvailable().then((available: boolean) => {
        if (available) {
          this.safariViewController
            .show({
              url: url,
              hidden: false,
              animated: false,
              enterReaderModeIfAvailable: true,
              tintColor: '#EF6C00',
              controlTintColor: '#EF6C00',
              barColor: '#FFFFFF',
              toolbarColor: '#FFFFFF',
            })
            .subscribe(
              (result: any) => {
                console.log(result);
              },
              (error: any) => console.error(error)
            );
        } else {
          window.open(url, '_system');
        }
      });
    } else {
      window.open(url, '_blank');
    }
  }

  /* PUSH
    -----------------------------------------------------------------*/

  initPushNotification(): void {
    if (this.user) {
      if (this.platform.is('cordova') && !this.device.isVirtual) {
        this.oneSignalNative = window.plugins.OneSignal;
        this.configureNativePush();
      } else if (this.platform.is('core')) {
        this.configureWebPush();
      }
    }
  }

  private async configureNativePush(): Promise<void> {
    try {
      this.oneSignalNative.initialize(ENV.push.nativeAppId);
      this.oneSignalNative.Notifications.addEventListener('click', (event: any) => {
        this.ngZone.run(() => {
          try {
            console.log('notification opened', event);
            this.prepareNativePush(event.notification, true);
          } catch (error) {
            console.error(error);
          }
        });
      });
      this.oneSignalNative.Notifications.addEventListener('foregroundWillDisplay', (event) => {
        this.ngZone.run(() => {
          try {
            console.log('notification received', event);
            // Use preventDefault() to not display
            event.preventDefault();
            // Use notification.display() to display the notification after some async work
            event.getNotification().display();
            this.prepareNativePush(event.notification, false);
          } catch (error) {
            console.error(error);
          }
        });
      });
      this.oneSignalNative.Notifications.addEventListener('permissionChange', (resp) => {
        console.log('Permission changed to ' + resp);
      });
      if (this.user) {
        const env: string = ENV.mode === 'PROD' ? 'PRD-' : 'HOM-';
        if (this.user.userId) {
          this.oneSignalNative.login(env + this.user.userId);
        }
        this.setAuthorityTagPushNotification();
      }
      let hasPermission = this.oneSignalNative.Notifications.hasPermission();
      console.log(hasPermission);
      if (!hasPermission) {
        if (await this.oneSignalNative.Notifications.canRequestPermission()) {
          console.log('VAI FAZER REQUEST PERMISSION');
          // Na versão 5.0.0, por alguma razão não está retornando a promisse, travando
          let permission = await this.oneSignalNative.Notifications.requestPermission(true);
          console.log('VOLTOU REQUEST PERMISSION', permission);
        }
      }
      this.oneSignalNative.User.pushSubscription.optIn();
    } catch (erro) {
      console.error(erro);
    }
  }

  private configureWebPush(): void {
    if (location.hostname !== 'localhost') {
      if (!this.pushRegistered || !this.oneSignalWeb) {
        this.oneSignalWeb = window['OneSignal'] || [];
        this.oneSignalWeb.push(() => {
          this.oneSignalWeb.SERVICE_WORKER_PARAM = { scope: '/push/onesignal/' };
          this.oneSignalWeb.SERVICE_WORKER_PATH = 'push/onesignal/OneSignalSDKWorker.js';
          this.oneSignalWeb.SERVICE_WORKER_UPDATER_PATH =
            'push/onesignal/OneSignalSDKUpdaterWorker.js';
          this.oneSignalWeb.init({
            appId: ENV.push.webAppId,
            safari_web_id: ENV.push.safari_web_id,
            autoRegister: false,
            autoResubscribe: true,
            allowLocalhostAsSecureOrigin: true,
            welcomeNotification: {
              disable: true,
            },
            persistNotification: false,
            // notificationClickHandlerMatch: 'origin',
            notificationClickHandlerAction: 'focus',
            promptOptions: {
              /* Change bold title, limited to 30 characters */
              siteName: 'Fastget',
              /* Subtitle, limited to 90 characters */
              actionMessage:
                'Gostaríamos de notificar você sobre pagamentos, novos pedidos, novidades e atualizações.',
              /* Example notification title */
              exampleNotificationTitle: 'Você recebeu um novo pedido!',
              /* Example notification message */
              exampleNotificationMessage: 'Pedido #12, verifique na aba de novos pedidos',
              /* Text below example notification, limited to 50 characters */
              exampleNotificationCaption: 'Recomendado para uma melhor experiência',
              /* Accept button text, limited to 15 characters */
              acceptButtonText: 'Aceitar',
              /* Cancel button text, limited to 15 characters */
              cancelButtonText: 'Não, obrigado',
              // autoAcceptTitle: '(não recomendado)'
            },
          });
        });
        this.oneSignalWeb.showHttpPrompt(); //{force: true}
        this.pushRegistered = true;
      }
      this.oneSignalWeb.on('subscriptionChange', (isSubscribed) => {
        console.log("The user's subscription state is now:", isSubscribed);
      });
      this.oneSignalWeb.on('notificationDisplay', (push: any) => {
        try {
          console.log('OneSignal notification displayed:', push);
          if (push && push.data && push.data.pushType && push.data.pushType === PushType.ORDERS) {
            const orderLocalConfig = OrderLocalConfig.get();
            const newOrderAudio: HTMLAudioElement = new Audio('assets/sound/bell.mp3');
            newOrderAudio.volume = orderLocalConfig.orderAudioVolume / 100;
            newOrderAudio.play();
          }
        } catch (error) {
          console.error(error);
        }
      });
      if (this.user) {
        const env: string = ENV.mode === 'PROD' ? 'PRD-' : 'HOM-';
        if (this.user.userId) {
          this.oneSignalWeb.setExternalUserId(env + this.user.userId);
        }
        this.setAuthorityTagPushNotification();
      }
      this.oneSignalWeb.setSubscription(true);
    }
  }

  private setAuthorityTagPushNotification(): void {
    if (
      this.userService.estabSelected &&
      this.userService.estabSelected.authority &&
      this.userService.estabSelected.authority['code'] != null
    ) {
      this.sendAuthorityTagPushNotification(this.userService.estabSelected.authority);
    } else if (
      this.userService.placeSelected &&
      this.userService.placeSelected.authority &&
      this.userService.placeSelected.authority['code'] != null
    ) {
      this.sendAuthorityTagPushNotification(this.userService.placeSelected.authority);
    }
  }

  private sendAuthorityTagPushNotification(authority: Authority): void {
    if (this.platform.is('cordova') && !this.device.isVirtual) {
      this.oneSignalNative.User.addTag('authority', String(authority['code']));
    } else if (this.platform.is('core') && this.oneSignalWeb) {
      this.oneSignalWeb.sendTag('authority', authority['code']);
    }
  }

  private prepareNativePush(notification: Notification, openedAction: boolean): void {
    const toastConfig: ToastOptions = {
      message: notification.body,
      duration: 8000,
      position: 'top',
      showCloseButton: true,
      closeButtonText: 'Ver',
    };
    if (notification.additionalData && notification.additionalData.pushType) {
      // ORDERS,
      // COUPON,
      // RATING,
      // NEWS,
      // CAMPAIGN
      switch (notification.additionalData.pushType) {
        case PushType.ORDERS:
          this.prepareOrderPushByScreen(toastConfig, notification, openedAction);
          break;

        case PushType.RATING:
          // Testar melhor
          // this.seeRatingsFromPush(toastConfig, notification, openedAction);
          break;

        case PushType.RESERVATION:
          if (notification.isAppInFocus) {
            this.showToast(toastConfig, () => {
              this.app.getActiveNav().setRoot('ReservationList');
            });
          }
          break;

        default:
          break;
      }
    }
  }

  // private async seeRatingsFromPush(toastConfig: any, notification: Notification, openedAction: boolean): Promise<void> {
  // 	try {
  // 		const seeRatings: VoidFunction = () => {
  // 			try {
  // 				this.menuPageSelected = "Rating";
  // 				this.app.getActiveNav().setRoot("Rating");
  // 			} catch (error) {
  // 				console.error(error);
  // 			}
  // 		};
  // 		if (openedAction) {
  // 			seeRatings();
  // 			if (this.platform.is("cordova")) {
  // 				this.oneSignalNative.Notifications.clearAll();
  // 			}
  // 		} else if (notification.isAppInFocus) {
  // 			this.showToast(toastConfig, () => {
  // 				seeRatings();
  // 			});
  // 		}
  // 	} catch (error) {
  // 		console.error(error);
  // 	}
  // }

  private async prepareOrderPushByScreen(
    toastConfig: any,
    notification: Notification,
    openedAction: boolean
  ): Promise<void> {
    try {
      const page: ViewController = this.app.getActiveNav().getActive();

      const goToOrders: VoidFunction = () => {
        try {
          this.menuPageSelected = 'Orders';
          this.app.getActiveNav().setRoot('Orders');
        } catch (error) {
          console.error(error);
        }
      };

      switch (page.id) {
        case 'Orders':
          // Trata no websocket já...
          // TODO: testar melhor, o app parece não atualizar se a tela estiver apagada.
          break;

        default:
          if (openedAction) {
            goToOrders();
            if (this.platform.is('cordova')) {
              this.oneSignalNative.Notifications.clearAll();
            }
          } else if (notification.isAppInFocus) {
            this.showToast(toastConfig, () => {
              goToOrders();
            });
          }
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  private unsubscribePush(): void {
    try {
      if (this.platform.is('cordova') && this.oneSignalNative) {
        this.oneSignalNative.logout();
        this.oneSignalNative.User.removeTags(['authority', 'storeId']);
        //this.oneSignalNative.User.pushSubscription.optOut();
      } else if (this.oneSignalWeb) {
        this.oneSignalWeb.removeExternalUserId();
        this.oneSignalWeb.deleteTags(['authority', 'storeId']);
        this.oneSignalWeb.disablePush(true);
      }
    } catch (erro) {
      console.log(erro);
    }
  }

  // CHECA VERSÃO
  // ------------------------------

  private async initFirebaseConfig(): Promise<void> {
    try {
      if (this.platform.is('cordova')) {
        // Fetch 1h em prod, e 10 minutos em homolog
        const fetchInterval: number = 600; //ENV.mode === 'PROD' ? 3600 : 600;
        await (window as any).cordova.plugins.firebase.config.fetch(fetchInterval);
        await (window as any).cordova.plugins.firebase.config.activate();
      }
    } catch (error) {
      console.error(error);
    }
  }

  async checkRemoteConfigs(): Promise<void> {
    if (this.platform.is('cordova')) {
      try {
        const keyNewsFirebase: string = 'show_sms_wallet_news';
        const showSmsWalletNews: boolean = await (
          window as any
        ).cordova.plugins.firebase.config.getBoolean(keyNewsFirebase);
        console.log(showSmsWalletNews);

        this.userService.localConfig.showSmsWalletNews = showSmsWalletNews;
        this.userService.updateLocalConfig();
      } catch (error) {
        console.error(error);
      }
    }
  }

  async checkVersion(): Promise<void> {
    if (this.platform.is('cordova')) {
      const localVersion: string = await this.appVersion.getVersionNumber();
      console.log('versaoLocal: ', localVersion);
      try {
        const keyFirebase: string = this.platform.is('android') ? 'android_version' : 'ios_version';
        const versionString: string = await (
          window as any
        ).cordova.plugins.firebase.config.getString(keyFirebase);
        console.log(versionString);

        if (versionString) {
          const remoteVersion: VersionFirebase = JSON.parse(versionString);
          if (remoteVersion) {
            this.userService.localConfig.remoteVersion = remoteVersion;
            this.userService.updateLocalConfig();
            this.compareVersion(localVersion, remoteVersion);
          }
        }
      } catch (error) {
        console.error(error);
        // No iOS, não faz o fetch sempre, e as vezes ocasiona em erros. Por isso é salvo no Storage a última versão para comparar
        this.compareVersion(localVersion, this.userService.localConfig.remoteVersion);
      }
    } else {
      console.log('swUpdate isEnabled: ' + this.swUpdate.isEnabled);
      if (this.swUpdate.isEnabled) {
        this.swUpdate.available.subscribe(async (event) => {
          console.log('current version is', event.current);
          console.log('available version is', event.available);

          if (!this.alreadyDisplayedAvailableUpdate) {
            this.alreadyDisplayedAvailableUpdate = true;
            this.showToast(
              {
                message:
                  'Olá, existe uma nova atualização disponível! Atualize para continuar utilizando.',
                position: 'top',
                showCloseButton: true,
                closeButtonText: 'Atualizar',
              },
              () => {
                this.updateApp();
              }
            );
          }
        });
        // Primeira vez, já checa.. depois somente a cada 6 horas se nao atualizar a página
        this.swUpdate.checkForUpdate();
        interval(6 * 60 * 60 * 1000).subscribe(() => {
          console.log('checando se tem nova versão');
          this.swUpdate.checkForUpdate();
        });
      }
    }
  }

  private async compareVersion(
    localVersion: string,
    remoteVersion: VersionFirebase
  ): Promise<void> {
    try {
      const result: VersionCompare = UtilHelper.compareVersions(
        localVersion,
        remoteVersion.currentVersion
      );
      switch (result) {
        case VersionCompare.REMOTE_VERSION_HIGHER:
          // Checa se a versão atual é obrigatória
          if (remoteVersion.isMandatory) {
            // TODO: Modal personalizada
            const alert: Alert = this.alertCtrl.create({
              title: 'Atualização',
              message:
                'Existe uma nova atualização no aplicativo Fastget. Atualize para continuar utilizando.',
              enableBackdropDismiss: false,
            });
            if (this.platform.is('android')) {
              alert.addButton({
                text: 'Mais tarde',
                role: 'cancel',
                handler: () => {
                  this.platform.exitApp();
                },
              });
            }
            alert.addButton({
              text: 'Atualizar',
              handler: () => {
                this.updateApp();
                return false;
              },
            });
            alert.present();
          } else {
            this.showToast(
              {
                message: 'Olá, existe uma nova versão do aplicativo Fastget',
                duration: 7000,
                position: 'bottom',
                showCloseButton: true,
                closeButtonText: 'Atualizar',
              },
              () => {
                this.updateApp();
              }
            );
          }
          break;

        case VersionCompare.LOCAL_VERSION_HIGHER:
          console.log(
            'ATUALIZE A VERSÃO REMOTA NO FIREBASE, a versão atual remota ' +
              remoteVersion.currentVersion +
              ' é INFERIOR a versão local ' +
              localVersion
          );
          break;

        case VersionCompare.IDENTICAL_VERSIONS:
          console.log(
            'versão atual remota ' +
              remoteVersion.currentVersion +
              ' é igual a versão local ' +
              localVersion
          );
          break;

        default:
          break;
      }
    } catch (error) {
      console.error(error);
    }
  }

  private async updateApp(): Promise<void> {
    const loading: Loading = this.loadingCtrl.create();
    try {
      await loading.present();
      if (this.platform.is('cordova')) {
        const appId: string = this.platform.is('ios')
          ? 'id1465848142'
          : 'br.com.fastget.partners.app';
        await this.market.open(appId);
      } else {
        document.location.reload();
      }
    } catch {
      this.showToast({
        message: 'Houve um erro ao tentar abrir a loja de aplicativos',
        duration: 3000,
      });
    } finally {
      loading.dismiss();
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts